/**
 * Modal Module
 * Handles modal dialog functionality for icons and notifications
 */

export class Modal {
    modal: HTMLElement | null;
    modalIcon: HTMLImageElement | null;
    modalThankYou: HTMLElement | null;
    closeButton: HTMLElement | null;
    escapeHandler: ((e: KeyboardEvent) => void) | null;
    countdownTimer: number | null;
    countdownCallback: (() => void) | null;
    modalCountdown: HTMLElement;
    
    constructor() {
        this.modal = document.getElementById('iconModal');
        this.modalIcon = document.getElementById('modalIcon') as HTMLImageElement | null;
        this.modalThankYou = document.getElementById('modalThankYou');
        this.closeButton = this.modal?.querySelector('.modal-close') as HTMLElement | null;
        this.escapeHandler = null;
        this.countdownTimer = null;
        this.countdownCallback = null;
        
        // Create countdown element
        this.modalCountdown = document.createElement('div');
        this.modalCountdown.className = 'modal-countdown hidden';
        this.modalCountdown.innerHTML = `
            <div class="countdown-circle">
                <svg class="countdown-svg" viewBox="0 0 100 100">
                    <circle class="countdown-bg" cx="50" cy="50" r="45" />
                    <circle class="countdown-progress" cx="50" cy="50" r="45" />
                </svg>
                <div class="countdown-number">30</div>
            </div>
            <div class="countdown-text">Your download will begin shortly...</div>
        `;
        
        // Add countdown to modal content
        if (this.modal) {
            const modalContent = this.modal.querySelector('.modal-content');
            if (modalContent) {
                modalContent.appendChild(this.modalCountdown);
            }
        }
        
        this.bindEvents();
    }

    /**
     * Bind modal events
     */
    bindEvents(): void {
        if (!this.modal || !this.closeButton) return;

        // Close button click
        this.closeButton.addEventListener('click', () => this.close());

        // Click outside modal to close
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.close();
            }
        });
    }

    /**
     * Show modal with specified content
     * @param type - Type of modal ('icon' or 'thank-you')
     * @param iconPath - Path to icon image (for icon type)
     */
    show(type: 'icon' | 'thank-you', iconPath: string | null = null): void {
        if (!this.modal) return;

        // Handle different modal types
        if (type === 'icon' && iconPath && this.modalIcon) {
            this.modalIcon.src = iconPath;
            this.modalIcon.classList.remove('hidden');
            this.modalThankYou?.classList.add('hidden');
        } else if (type === 'thank-you') {
            this.modalIcon?.classList.add('hidden');
            this.modalThankYou?.classList.remove('hidden');
        }

        // Show the modal
        this.modal.style.display = 'flex';

        // Set up escape key handler
        this.escapeHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.close();
            }
        };
        document.addEventListener('keydown', this.escapeHandler);

        // Focus management for accessibility
        this.closeButton?.focus();
    }

    /**
     * Close the modal
     */
    close(): void {
        if (!this.modal) return;

        this.cancelCountdown();
        this.modal.style.display = 'none';
        
        // Clean up escape key handler
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
            this.escapeHandler = null;
        }
    }

    /**
     * Show icon modal
     * @param iconPath - Path to the icon image
     */
    showIcon(iconPath: string): void {
        this.show('icon', iconPath);
    }

    /**
     * Show thank you modal
     */
    showThankYou(): void {
        this.show('thank-you');
    }

    /**
     * Check if modal is currently open
     * @returns True if modal is open
     */
    isOpen(): boolean {
        return this.modal !== null && this.modal.style.display === 'flex';
    }

    /**
     * Show countdown timer and execute callback when finished
     * @param callback - Function to execute after countdown
     */
    showCountdown(callback: () => void): void {
        if (!this.modal) return;
        
        // Show thank you message and countdown simultaneously
        this.modalIcon?.classList.add('hidden');
        this.modalThankYou?.classList.remove('hidden'); // Show thank you message
        this.modalCountdown.classList.remove('hidden');
        
        // Store callback for later execution
        this.countdownCallback = callback;
        
        // Show the modal
        this.modal.style.display = 'flex';
        
        // Initialize countdown variables
        let secondsLeft = 30;
        const countdownNumber = this.modalCountdown.querySelector('.countdown-number') as HTMLElement;
        const countdownProgress = this.modalCountdown.querySelector('.countdown-progress') as HTMLElement;
        
        // Set initial values
        if (countdownNumber) countdownNumber.textContent = secondsLeft.toString();
        if (countdownProgress) {
            // Reset animation
            countdownProgress.style.strokeDasharray = '283';  // Circumference of circle (2πr = 2π*45 ≈ 283)
            countdownProgress.style.strokeDashoffset = '0';
            countdownProgress.style.transition = 'stroke-dashoffset 1s linear';
        }
        
        // Start the countdown
        this.countdownTimer = window.setInterval(() => {
            secondsLeft--;
            
            // Update countdown display
            if (countdownNumber) countdownNumber.textContent = secondsLeft.toString();
            if (countdownProgress) {
                const progress = (secondsLeft / 30);
                const dashoffset = 283 * (1 - progress);
                countdownProgress.style.strokeDashoffset = dashoffset.toString();
            }
            
            // When countdown reaches zero
            if (secondsLeft <= 0) {
                if (this.countdownTimer !== null) {
                    clearInterval(this.countdownTimer);
                    this.countdownTimer = null;
                }
                
                // Hide countdown but keep thank you message visible
                this.modalCountdown.classList.add('hidden');
                
                // Execute callback
                if (typeof this.countdownCallback === 'function') {
                    this.countdownCallback();
                    this.countdownCallback = null;
                }
            }
        }, 1000);
    }
    
    /**
     * Cancel countdown if active
     */
    cancelCountdown(): void {
        if (this.countdownTimer !== null) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
            this.countdownCallback = null;
            this.modalCountdown.classList.add('hidden');
        }
    }
}
